import { useEffect, useRef, useCallback } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'

type EventHandler = (...args: unknown[]) => void

interface UploadNotification {
  assetId: string
  versionId: string
  status: 'Completed' | 'Failed'
  versionNumber: number
  errorMessage?: string | null
}

/**
 * A QA run has landed for a version of this asset (ORB-889).
 *
 * Ids only, deliberately. A run is a persisted document with a GET behind it, so the handler
 * refetches rather than patching from this — which is also the only safe shape here, because the
 * `asset-` group carries no ownership check and a run is workspace-private. See
 * docs/REALTIME-SIGNALR.md.
 */
interface QaCompletedNotification {
  AssetId: string
  AssetVersionId: string
  VersionNumber: number
}

interface UseAssetSignalROptions {
  assetId: string | undefined
  userId: string | undefined
  /** Needed for the QA subscription, which uses the authorized workspace group. */
  workspaceId?: string
  onCommentAdded: (comment: unknown) => void
  onUploadCompleted: (notification: UploadNotification) => void
  onUploadFailed: (notification: UploadNotification) => void
  /** Called when a QA run finishes for this asset, so an open panel can refetch it (ORB-889). */
  onQaCompleted?: (notification: QaCompletedNotification) => void
}

/**
 * Manages SignalR group subscriptions and event handlers for asset review.
 *
 * Handles:
 * - Joining/leaving the asset group for real-time comments
 * - Tracking upload status handlers and cleaning them up on unmount/asset change
 * - Pre-joining user group for URL uploads (race condition prevention)
 */
export function useAssetSignalR({
  assetId,
  userId,
  workspaceId,
  onCommentAdded,
  onUploadCompleted,
  onUploadFailed,
  onQaCompleted,
}: UseAssetSignalROptions) {
  // Track active upload handler so we can clean it up on unmount or asset change
  const uploadHandlerRef = useRef<{ handler: EventHandler; group: string } | null>(null)

  const cleanupUploadHandler = useCallback(() => {
    const entry = uploadHandlerRef.current
    if (entry) {
      signalRService.off('UploadStatusChanged', entry.handler)
      signalRService.leaveGroup(entry.group)
      uploadHandlerRef.current = null
    }
  }, [])

  // Subscribe to real-time comments for the current asset
  useEffect(() => {
    if (!assetId) return
    const group = `asset-${assetId}`
    let cancelled = false

    const handleCommentAdded = onCommentAdded as EventHandler

    // ⚑ Handlers are registered INSIDE the then(). signalRService.on() is `this.connection?.on(...)`,
    // so registering before a connection exists is a silent no-op with no error and total silence.
    signalRService.joinGroup(group).then(() => {
      if (cancelled) {
        signalRService.leaveGroup(group)
        return
      }
      signalRService.on('CommentAdded', handleCommentAdded)
    })

    return () => {
      cancelled = true
      signalRService.off('CommentAdded', handleCommentAdded)
      signalRService.leaveGroup(group)
    }
  }, [assetId, onCommentAdded])

  /**
   * A finished QA run, on the **workspace** group (ORB-889).
   *
   * ⚑⚑ Separate from the comment subscription above on purpose, because it uses a different group.
   * `workspace-` is the only prefix `RealtimeFunction:48` authorizes — it checks the caller's
   * accessible workspaces and fails closed — while `asset-` is resource-opaque, so any authenticated
   * caller who knows an asset id receives its events. This event names a version id and number the
   * subscriber would not otherwise have. Raised by Copilot on PR #144.
   *
   * ⚑ Filtering on the asset id is mandatory, not defensive: the group is now workspace-wide, so this
   * handler receives the QA events of every asset in the workspace. It was already required — one
   * shared connection fires every handler for every joined group — and now it is load bearing.
   */
  useEffect(() => {
    if (!workspaceId || !assetId || !onQaCompleted) return
    const group = `workspace-${workspaceId}`
    let cancelled = false

    const handleQaCompleted = ((raw: unknown) => {
      const notification = raw as QaCompletedNotification | undefined
      if (!notification || notification.AssetId !== assetId) return
      onQaCompleted(notification)
    }) as EventHandler

    signalRService.joinGroup(group).then(() => {
      if (cancelled) {
        signalRService.leaveGroup(group)
        return
      }
      signalRService.on('AssetQaCompleted', handleQaCompleted)
    })

    return () => {
      cancelled = true
      signalRService.off('AssetQaCompleted', handleQaCompleted)
      signalRService.leaveGroup(group)
    }
  }, [workspaceId, assetId, onQaCompleted])

  // Clean up any lingering upload handler when asset changes or component unmounts
  useEffect(() => {
    return () => cleanupUploadHandler()
  }, [assetId, cleanupUploadHandler])

  /**
   * Subscribe to upload status for a pending version (e.g. after loading asset data
   * and finding an in-progress URL upload).
   */
  const subscribeToUploadStatus = useCallback((versionId: string) => {
    if (!userId) return

    // Clean up any previous upload subscription
    cleanupUploadHandler()

    const group = `user-${userId}`

    signalRService.joinGroup(group).then(() => {
      const handleUploadResult: EventHandler = (payload: unknown) => {
        const notification = payload as UploadNotification
        if (notification.versionId !== versionId) return

        // Self-cleanup on receipt
        cleanupUploadHandler()

        if (notification.status === 'Completed') {
          onUploadCompleted(notification)
        } else {
          onUploadFailed(notification)
        }
      }

      signalRService.on('UploadStatusChanged', handleUploadResult)
      uploadHandlerRef.current = { handler: handleUploadResult, group }
    })
  }, [userId, cleanupUploadHandler, onUploadCompleted, onUploadFailed])

  /**
   * Pre-join the user group BEFORE an upload API call to prevent race conditions
   * where small files finish before the client subscribes.
   * Returns a cleanup function if the upload API call fails.
   */
  const preJoinForUpload = useCallback(async (versionId: string): Promise<() => void> => {
    if (!userId) return () => {}

    // Clean up any previous upload subscription
    cleanupUploadHandler()

    const group = `user-${userId}`
    await signalRService.joinGroup(group)

    const handleUploadResult: EventHandler = (payload: unknown) => {
      const notification = payload as UploadNotification
      if (notification.versionId !== versionId) return

      cleanupUploadHandler()

      if (notification.status === 'Completed') {
        onUploadCompleted(notification)
      } else {
        onUploadFailed(notification)
      }
    }

    signalRService.on('UploadStatusChanged', handleUploadResult)
    uploadHandlerRef.current = { handler: handleUploadResult, group }

    // Return cleanup for use if the API call fails
    return () => cleanupUploadHandler()
  }, [userId, cleanupUploadHandler, onUploadCompleted, onUploadFailed])

  return {
    subscribeToUploadStatus,
    preJoinForUpload,
  }
}
